import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { conversationService, channelService, tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { Badge } from "@/components/common/DataTable";
import { PROVIDER_COLOR } from "./ChannelsPage";
import ConversationPanel from "./ConversationPanel";
import { toast } from "sonner";
import { Plus, Search, Loader2, Inbox as InboxIcon } from "lucide-react";

const PRIORITY_COLOR = { Low: "slate", Normal: "blue", High: "amber", Urgent: "red" };
const STATUSES = ["Open", "Pending", "Resolved", "Closed"];
const EMPTY = { channelId: "", externalId: "", displayName: "", subject: "", priority: "Normal", initialMessage: "", tenantId: "" };

export default function InboxPage() {
  const { hasPermission, user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [scope, setScope] = useState("all"); // all | mine | unassigned
  const [q, setQ] = useState("");
  const [channels, setChannels] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const agentFilter = searchParams.get("assignedAgentId");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { size: 100, q: q || undefined, status: statusFilter || undefined };
      if (scope === "mine") params.mine = true;
      if (scope === "unassigned") params.unassigned = true;
      if (agentFilter) params.assignedAgentId = agentFilter;
      const d = await conversationService.list(params);
      setRows(d.items || []);
      setSelected((cur) => cur || (d.items && d.items[0] ? d.items[0].id : null));
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [q, statusFilter, scope, agentFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { channelService.list().then((d) => setChannels(d.items || [])).catch(() => {}); }, []);
  useEffect(() => { if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper]);

  const canCreate = hasPermission("CONVERSATION.CREATE");

  const create = async () => {
    if (!form.channelId) return toast.error("Select a channel");
    if (!form.externalId) return toast.error("Participant address is required");
    if (isSuper && !form.tenantId) return toast.error("Select a tenant");
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      const c = await conversationService.create(payload);
      toast.success("Conversation created");
      setShowNew(false); setForm(EMPTY); setSelected(c.id); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  // channels filtered to selected tenant in new-conversation modal
  const modalChannels = isSuper ? channels.filter((c) => !form.tenantId || c.tenantId === form.tenantId) : channels;

  return (
    <div className="h-[calc(100vh-7rem)] flex flex-col" data-testid="inbox-page">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Unified Inbox</h1>
          <p className="text-sm text-muted-foreground">All channels, one conversation workspace</p>
        </div>
        {canCreate && (
          <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors" data-testid="conv-create-btn">
            <Plus className="h-4 w-4" /> New Conversation
          </button>
        )}
      </div>

      <div className="flex-1 flex border border-border rounded-xl overflow-hidden bg-card min-h-0">
        {/* Conversation list */}
        <div className="w-80 shrink-0 border-r border-border flex flex-col min-h-0" data-testid="conversation-list">
          <div className="p-3 border-b border-border space-y-2">
            <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-2.5 py-1.5">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search subject…" className="bg-transparent text-sm outline-none w-full text-foreground" data-testid="conv-search" />
            </div>
            <div className="flex gap-1">
              {["all", "mine", "unassigned"].map((s) => (
                <button key={s} onClick={() => setScope(s)} className={`flex-1 text-xs py-1.5 rounded-md font-medium capitalize transition-colors ${scope === s ? "bg-primary text-primary-foreground" : "bg-muted/60 text-muted-foreground hover:bg-muted"}`} data-testid={`conv-scope-${s}`}>{s}</button>
              ))}
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full bg-muted/50 border border-border rounded-lg px-2 py-1.5 text-sm text-foreground" data-testid="conv-status-filter">
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading && <div className="flex justify-center py-10 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>}
            {!loading && rows.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <InboxIcon className="h-8 w-8 mb-2 opacity-40" /><p className="text-sm">No conversations</p>
              </div>
            )}
            {rows.map((c) => (
              <button key={c.id} onClick={() => setSelected(c.id)}
                className={`w-full text-left px-4 py-3 border-b border-border transition-colors ${selected === c.id ? "bg-primary/10" : "hover:bg-muted/50"}`}
                data-testid={`conv-item-${c.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm text-foreground truncate">{c.subject}</span>
                  {c.unreadCount > 0 && <span className="bg-primary text-primary-foreground text-[10px] rounded-full px-1.5 py-0.5 shrink-0" data-testid={`conv-unread-${c.id}`}>{c.unreadCount}</span>}
                </div>
                <p className="text-xs text-muted-foreground truncate mt-0.5">{c.lastMessagePreview || "—"}</p>
                <div className="flex items-center gap-1.5 mt-1.5">
                  <Badge color={PROVIDER_COLOR[c.providerType] || "slate"}>{c.providerType}</Badge>
                  <Badge color={PRIORITY_COLOR[c.priority] || "slate"}>{c.priority}</Badge>
                  {!c.assignedAgentId && <span className="text-[10px] text-rose-500 font-medium">unassigned</span>}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Conversation panel */}
        {selected ? (
          <ConversationPanel key={selected} conversationId={selected} onChange={load} />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
            <InboxIcon className="h-10 w-10 mb-3 opacity-40" />
            <p className="text-sm">Select a conversation to view the thread</p>
          </div>
        )}
      </div>

      {showNew && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="new-conv-dialog">
            <h3 className="text-lg font-bold mb-4">New Conversation</h3>
            <div className="space-y-3.5">
              {isSuper && (
                <Field label="Tenant *">
                  <select value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value, channelId: "" })} className="ciq-input" data-testid="new-conv-tenant">
                    <option value="">Select tenant…</option>
                    {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Channel *">
                <select value={form.channelId} onChange={(e) => setForm({ ...form, channelId: e.target.value })} className="ciq-input" data-testid="new-conv-channel">
                  <option value="">Select channel…</option>
                  {modalChannels.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.providerType})</option>)}
                </select>
              </Field>
              <Field label="Participant address * (email / phone / handle)">
                <input value={form.externalId} onChange={(e) => setForm({ ...form, externalId: e.target.value })} className="ciq-input" placeholder="e.g. customer@example.com" data-testid="new-conv-external" />
              </Field>
              <Field label="Display name"><input value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} className="ciq-input" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Subject"><input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="ciq-input" data-testid="new-conv-subject" /></Field>
                <Field label="Priority"><select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="ciq-input">{["Low", "Normal", "High", "Urgent"].map((p) => <option key={p} value={p}>{p}</option>)}</select></Field>
              </div>
              <Field label="Initial message"><textarea value={form.initialMessage} onChange={(e) => setForm({ ...form, initialMessage: e.target.value })} className="ciq-input" rows={2} data-testid="new-conv-message" /></Field>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowNew(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Cancel</button>
              <button onClick={create} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="new-conv-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-foreground mb-1">{label}</label>{children}</div>;
}
